import {
  ACTION_ORIGIN_LABELS,
  INITIATIVE_STATUS_LABELS,
  OBJECTIVE_HORIZON_LABELS,
  OBJECTIVE_LEVEL_LABELS,
  OBJECTIVE_RELATION_LABELS,
  OBJECTIVE_STATUS_LABELS,
  TREND_LABELS,
  actionPlanListSchema,
  formatDate,
  formatDateTime,
  initiativeListSchema,
  kpiTrend,
  objectiveChangesSchema,
  objectivePanelSchema,
  objectiveSchema,
  targetPeriodLabel,
  type Objective,
  type ObjectivePanel,
} from '@fluxus/shared';
import { keepPreviousData, useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { ArrowLeft, ChevronRight, FolderKanban, History, ListChecks, Rocket, Target } from 'lucide-react';
import { useEffect, useId, useState, type ReactNode } from 'react';
import { Link, useParams, useSearchParams } from 'react-router';
import { PageContainer, PageHeader } from '@/components/layout/PageHeader';
import { ProgressBar } from '@/components/progress/ProgressBar';
import { EmptyState, ErrorState, LoadingState } from '@/components/states/States';
import { StatusBadge } from '@/components/status/StatusBadge';
import { Button } from '@/components/ui/button';
import { NativeSelect } from '@/components/ui/select';
import { TabList, tabPanelProps } from '@/components/ui/tabs';
import { AUDIT_SOURCE_LABELS } from '@/features/audit/labels';
import { CollaborationPanel } from '@/features/collaboration/CollaborationPanel';
import { INITIATIVE_STATUS_LIGHT } from '@/features/initiatives/status';
import { ObjectiveAnalyses } from '@/features/meetings/CriticalAnalysis';
import { DecisionHistoryList } from '@/features/meetings/Minutes';
import { OBJECTIVE_STATUS_LIGHT } from '@/features/objectives/status';
import { MONTHS, Trend, lightLabel, withUnit } from '@/features/objectives/performance';
import { DRILL_PARAMS, drillHref, readPeriod, type DrillPath } from '@/features/drill/drill';
import { DrillBadgeLink } from '@/features/drill/DrillLinks';
import { DrillTrail } from '@/features/drill/DrillTrail';
import { useCurrentProject } from '@/features/workspace/workspace';
import { AuditLink } from '@/features/audit/AuditLink';
import { useAuth, useCan } from '@/lib/auth/session';

type Tab = 'summary' | 'kpis' | 'initiatives' | 'actions' | 'history' | 'analyses' | 'collab';
const TABS: { key: Tab; label: string }[] = [
  { key: 'summary', label: 'Resumo' },
  { key: 'kpis', label: 'Indicadores' },
  { key: 'initiatives', label: 'Iniciativas' },
  { key: 'actions', label: 'Ações' },
  { key: 'history', label: 'Histórico' },
  { key: 'analyses', label: 'Análises críticas' },
  { key: 'collab', label: 'Comentários e anexos' },
];
const isTab = (v: string | null): v is Tab => TABS.some((t) => t.key === v);

/**
 * Estratégia › Objetivos › detalhe (T-046b, RF-19; DEC-052): cabeçalho com perspectiva, performance do mês,
 * tendência, responsável e período, e as abas Resumo, Indicadores, Iniciativas, Ações, Histórico, Análises críticas
 * e Comentários e anexos — tudo da API, com carregando, vazio e erro em cada aba.
 */
export function ObjetivoDetalhePage() {
  const { organizationId = '', objectiveId = '' } = useParams();
  const { workspace, projects, project } = useCurrentProject(organizationId);
  const back = `/empresas/${organizationId}/estrategia/objetivos`;
  return (
    <PageContainer className="max-w-6xl">
      <Link
        to={back}
        className="inline-flex items-center gap-1 text-sm text-muted-foreground underline-offset-2 hover:underline"
      >
        <ArrowLeft className="size-4" aria-hidden />
        Voltar para Objetivos
      </Link>
      {workspace.isPending ? (
        <LoadingState className="mt-6" rows={4} label="Carregando o projeto" />
      ) : workspace.isError ? (
        <ErrorState
          className="mt-6"
          error={workspace.error}
          onRetry={() => void workspace.refetch()}
          retrying={workspace.isFetching}
        />
      ) : !project ? (
        <EmptyState
          className="mt-6 rounded-lg border border-dashed border-border py-12"
          icon={FolderKanban}
          title="Nenhum projeto BSC"
          description="Os objetivos pertencem a um projeto. Quando a empresa tiver um projeto, ele aparece aqui."
        />
      ) : (
        <div key={project.id} className="mt-3 flex flex-col gap-4">
          {/* O objetivo é de um projeto (o do endereço): aqui não se troca de projeto, só se informa qual é. */}
          {projects.length > 1 ? (
            <p className="text-sm text-muted-foreground">Projeto: {project.name}</p>
          ) : null}
          <ObjectiveDetail
            // Outro objetivo (link de pai, filho ou relação): estado da tela do zero.
            key={objectiveId}
            organizationId={organizationId}
            projectId={project.id}
            horizon={[project.horizonStart, project.horizonEnd]}
            objectiveId={objectiveId}
          />
        </div>
      )}
    </PageContainer>
  );
}

function ObjectiveDetail({
  organizationId,
  projectId,
  horizon,
  objectiveId,
}: {
  organizationId: string;
  projectId: string;
  horizon: [number, number];
  objectiveId: string;
}) {
  const { client } = useAuth();
  const can = useCan();
  const base = `/organizations/${organizationId}/projects/${projectId}`;
  const now = new Date();
  const [params, setParams] = useSearchParams();
  // O período do link (drill-down — T-144b, DEC-058) vale quando está no horizonte; senão, o mês atual.
  const [asked] = useState(() => {
    const p = readPeriod(params);
    return p && p.year >= horizon[0] && p.year <= horizon[1] ? p : null;
  });
  const [year, setYearState] = useState(
    asked?.year ?? Math.min(Math.max(now.getFullYear(), horizon[0]), horizon[1]),
  );
  const [month, setMonthState] = useState(() =>
    asked ? asked.month : year === now.getFullYear() ? now.getMonth() + 1 : year < now.getFullYear() ? 12 : 1,
  );
  // O período escolhido vai ao endereço: o breadcrumb e os links levam o mesmo mês.
  const setPeriod = (y: number, m: number) => {
    setYearState(y);
    setMonthState(m);
    setParams(
      (prev) => {
        const out = new URLSearchParams(prev);
        out.set(DRILL_PARAMS.year, String(y));
        out.set(DRILL_PARAMS.month, String(m));
        return out;
      },
      { replace: true },
    );
  };
  // Um link para a mesma tela com outro período (sem remontar) passa a valer.
  const linkYear = params.get(DRILL_PARAMS.year);
  const linkMonth = params.get(DRILL_PARAMS.month);
  const [firstYear, lastYear] = horizon;
  useEffect(() => {
    const p = readPeriod(new URLSearchParams({ ano: linkYear ?? '', mes: linkMonth ?? '' }));
    if (!p || p.year < firstYear || p.year > lastYear) return;
    setYearState(p.year);
    setMonthState(p.month);
  }, [linkYear, linkMonth, firstYear, lastYear]);
  const setYear = (y: number) => setPeriod(y, month);
  const setMonth = (m: number) => setPeriod(year, m);
  const requested = params.get('aba');
  const tabs = TABS.filter((t) => t.key !== 'analyses' || can('analise-critica', 'view'));
  const [tab, setTab] = useState<Tab>(() =>
    isTab(requested) && tabs.some((t) => t.key === requested) ? requested : 'summary',
  );
  // Abas visitadas ficam montadas: o que foi digitado num comentário não se perde ao trocar de aba.
  const [seen, setSeen] = useState<ReadonlySet<Tab>>(() => new Set<Tab>([tab]));
  const showTab = (next: Tab) => {
    setTab(next);
    setSeen((prev) => new Set(prev).add(next));
    setParams(
      (prev) => {
        const out = new URLSearchParams(prev);
        if (next === 'summary') out.delete('aba');
        else out.set('aba', next);
        return out;
      },
      { replace: true },
    );
  };
  const tabsId = useId();
  const ids = { year: useId(), month: useId() };
  const years = Array.from({ length: horizon[1] - horizon[0] + 1 }, (_, i) => horizon[0] + i);

  const objective = useQuery({
    queryKey: ['objectives', projectId, 'detail', objectiveId],
    queryFn: ({ signal }) =>
      client.request(`${base}/objectives/${objectiveId}`, { schema: objectiveSchema, signal }),
  });
  const performance = useQuery({
    queryKey: ['objectives', projectId, 'performance', objectiveId, year, month],
    queryFn: ({ signal }) =>
      client.request(`${base}/objectives/${objectiveId}/performance`, {
        query: { year: String(year), month: String(month) },
        schema: objectivePanelSchema,
        signal,
      }),
    placeholderData: keepPreviousData,
    enabled: objective.isSuccess,
  });

  if (objective.isPending) return <LoadingState rows={5} label="Carregando o objetivo" />;
  if (objective.isError) {
    return (
      <ErrorState
        error={objective.error}
        onRetry={() => void objective.refetch()}
        retrying={objective.isFetching}
      />
    );
  }
  const o = objective.data;
  const p = performance.data;
  const trend = p ? trendOf(p, month) : null;
  const period =
    o.startDate || o.endDate
      ? `${o.startDate ? formatDate(o.startDate) : '…'} a ${o.endDate ? formatDate(o.endDate) : '…'}`
      : `Horizonte do projeto: ${horizon[0]}–${horizon[1]}`;

  return (
    <article aria-label={`Objetivo ${o.name}`} className="flex flex-col gap-4">
      <PageHeader
        title={o.name}
        description={o.description ?? undefined}
        actions={
          <AuditLink
            organizationId={organizationId}
            scope={{ entity: 'objective', record: objectiveId, name: o.name }}
            label={`Auditoria de ${o.name}`}
          />
        }
      />
      <DrillTrail organizationId={organizationId} path={{ projectId, objectiveId, year, month }} />
      <div className="flex flex-wrap items-center gap-2 text-xs">
        <StatusBadge status={OBJECTIVE_STATUS_LIGHT[o.status]} label={OBJECTIVE_STATUS_LABELS[o.status]} />
        {o.archivedAt ? <StatusBadge status="gray" label="Arquivado" /> : null}
        <span className="text-muted-foreground">{OBJECTIVE_LEVEL_LABELS[o.level]}</span>
      </div>
      <section aria-label="Resumo do objetivo" className="rounded-lg border border-border bg-surface p-4">
        <dl className="grid gap-x-6 gap-y-3 text-sm sm:grid-cols-2 lg:grid-cols-3">
          <Fact label="Perspectiva">{o.perspective.name}</Fact>
          <Fact label="Responsável">{o.owner?.name ?? 'Sem responsável'}</Fact>
          <Fact label="Período">{period}</Fact>
          <Fact label={`Performance em ${targetPeriodLabel('MONTH', month).toLowerCase()}/${year}`}>
            {performance.isPending ? (
              <span className="text-muted-foreground">Carregando…</span>
            ) : performance.isError ? (
              <span className="text-status-red-fg">Não foi possível carregar</span>
            ) : p?.month ? (
              <button
                type="button"
                onClick={() => {
                  if (p.source !== 'CHILDREN') return showTab('kpis');
                  showTab('summary');
                  // Já no Resumo: rola e leva o foco até os objetivos que o desdobram.
                  requestAnimationFrame(() => {
                    const target = document.getElementById(CHILDREN_ANCHOR);
                    target?.focus();
                    target?.scrollIntoView?.({ behavior: 'smooth', block: 'start' });
                  });
                }}
                aria-label={`Performance ${lightLabel(p.month.performance, p.month.light)}: ver ${
                  p.source === 'CHILDREN' ? 'os objetivos que o desdobram' : 'os indicadores que a compõem'
                }`}
                className="inline-flex items-center gap-0.5 rounded-full hover:ring-2 hover:ring-ring/40 focus-visible:ring-2 focus-visible:ring-ring/60"
              >
                <StatusBadge
                  status={p.month.light}
                  label={lightLabel(p.month.performance, p.month.light)}
                  size="md"
                />
                <ChevronRight aria-hidden className="size-4 text-muted-foreground" />
              </button>
            ) : (
              <StatusBadge status="gray" label="Sem cálculo no mês" size="md" />
            )}
          </Fact>
          <Fact label="Tendência">
            {trend
              ? `${TREND_LABELS[trend.direction]}${trend.favorable === null ? '' : trend.favorable ? ' (favorável)' : ' (desfavorável)'}`
              : 'Sem dados suficientes'}
          </Fact>
          <Fact label="Área">{o.department?.name ?? 'Empresa toda'}</Fact>
        </dl>
        <div className="mt-4 flex flex-wrap items-end gap-3">
          <div className="flex flex-col gap-1">
            <label htmlFor={ids.year} className="text-xs font-medium text-muted-foreground">
              Ano
            </label>
            <NativeSelect id={ids.year} value={year} onChange={(e) => setYear(Number(e.target.value))}>
              {years.map((y) => (
                <option key={y} value={y}>
                  {y}
                </option>
              ))}
            </NativeSelect>
          </div>
          <div className="flex flex-col gap-1">
            <label htmlFor={ids.month} className="text-xs font-medium text-muted-foreground">
              Mês
            </label>
            <NativeSelect id={ids.month} value={month} onChange={(e) => setMonth(Number(e.target.value))}>
              {MONTHS.map((m) => (
                <option key={m} value={m}>
                  {targetPeriodLabel('MONTH', m)}
                </option>
              ))}
            </NativeSelect>
          </div>
        </div>
      </section>

      <TabList id={tabsId} label={`Seções de ${o.name}`} tabs={tabs} value={tab} onChange={showTab} />
      {tabs.map((t) =>
        // Aba ainda não visitada: o painel existe (vazio e oculto), para o aria-controls da aba apontar para ele.
        !seen.has(t.key) ? (
          <div key={t.key} {...tabPanelProps(tabsId, t.key)} hidden />
        ) : (
          <div key={t.key} {...tabPanelProps(tabsId, t.key)} hidden={tab !== t.key}>
            {t.key === 'summary' ? (
              <SummaryTab
                organizationId={organizationId}
                projectId={projectId}
                objective={o}
                query={performance}
                year={year}
                month={month}
              />
            ) : t.key === 'kpis' ? (
              <KpisTab
                organizationId={organizationId}
                path={{ projectId, objectiveId, year, month }}
                query={performance}
              />
            ) : t.key === 'initiatives' ? (
              <InitiativesTab
                organizationId={organizationId}
                base={base}
                projectId={projectId}
                objectiveId={o.id}
              />
            ) : t.key === 'actions' ? (
              <ActionsTab
                organizationId={organizationId}
                base={base}
                projectId={projectId}
                objectiveId={o.id}
              />
            ) : t.key === 'history' ? (
              <HistoryTab
                base={base}
                projectId={projectId}
                objectiveId={o.id}
                query={performance}
                year={year}
                month={month}
              />
            ) : t.key === 'analyses' ? (
              <div className="flex flex-col gap-6">
                <section aria-label="Itens discutidos nas análises críticas" className="flex flex-col gap-2">
                  <h2 className="text-base font-bold">Itens discutidos</h2>
                  <ObjectiveAnalyses
                    organizationId={organizationId}
                    projectId={projectId}
                    objectiveId={o.id}
                  />
                </section>
                <section aria-label="Decisões sobre o objetivo" className="flex flex-col gap-2">
                  <h2 className="text-base font-bold">Decisões</h2>
                  <DecisionHistoryList
                    organizationId={organizationId}
                    projectId={projectId}
                    subject="objectives"
                    subjectId={o.id}
                    label={o.name}
                  />
                </section>
              </div>
            ) : (
              <CollaborationPanel
                organizationId={organizationId}
                projectId={projectId}
                subject="objective"
                subjectId={o.id}
                label={o.name}
              />
            )}
          </div>
        ),
      )}
    </article>
  );
}

/** Tendência da performance do objetivo até o mês escolhido (meses com dado, maior é melhor). */
function trendOf(p: ObjectivePanel, month: number) {
  const values = p.trend
    .filter((t) => t.month <= month && t.performance !== null)
    .map((t) => t.performance as number);
  return kpiTrend(values, 'HIGHER_BETTER');
}

function Fact({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div>
      <dt className="text-xs text-muted-foreground">{label}</dt>
      <dd className="mt-0.5">{children}</dd>
    </div>
  );
}

type PanelQuery = ReturnType<typeof useQuery<ObjectivePanel>>;

/** Carregando e erro do desempenho do mês, comuns às abas que o usam. */
function PanelState({ query, children }: { query: PanelQuery; children: (p: ObjectivePanel) => ReactNode }) {
  if (query.isPending) return <LoadingState rows={3} label="Carregando o desempenho do objetivo" />;
  if (query.isError) {
    return (
      <ErrorState error={query.error} onRetry={() => void query.refetch()} retrying={query.isFetching} />
    );
  }
  return <>{children(query.data)}</>;
}

/** Âncora dos objetivos que desdobram (a performance do objetivo consolidado leva até eles). */
const CHILDREN_ANCHOR = 'objetivos-que-desdobram';

function SummaryTab({
  organizationId,
  projectId,
  objective: o,
  query,
  year,
  month,
}: {
  organizationId: string;
  projectId: string;
  objective: Objective;
  query: PanelQuery;
  year: number;
  month: number;
}) {
  // Com o mês (drill-down — T-144c).
  const link = (id: string) =>
    drillHref.objective(organizationId, { projectId, objectiveId: id, year, month });
  const seesHistory = useCan()('metas-resultados', 'view');
  const relations = [
    ...o.relations.map((r) => ({ ...r, direction: 'para' as const })),
    ...o.relatedFrom.map((r) => ({ ...r, direction: 'de' as const })),
  ];
  return (
    <div className="flex flex-col gap-5 text-sm">
      <dl className="grid gap-x-6 gap-y-3 sm:grid-cols-2 lg:grid-cols-3">
        <Fact label="Horizonte">{OBJECTIVE_HORIZON_LABELS[o.horizon]}</Fact>
        <Fact label="Peso na perspectiva">{o.weight !== null ? `${o.weight}%` : 'Sem peso definido'}</Fact>
        <Fact label="Objetivo-pai">
          {o.parent ? (
            <Link className="inline-flex items-center underline underline-offset-2" to={link(o.parent.id)}>
              {o.parent.name}
            </Link>
          ) : o.parentHidden ? (
            'De uma área fora do seu escopo'
          ) : (
            'Nenhum'
          )}
        </Fact>
      </dl>
      {o.notes ? (
        <p className="whitespace-pre-line break-words">
          <span className="font-semibold">Observações: </span>
          {o.notes}
        </p>
      ) : null}
      <PanelState query={query}>
        {(p) => (
          <>
            {p.source === 'CHILDREN' ? (
              <p className="rounded-md bg-muted px-3 py-2 text-xs">
                Sem KPI próprio: a performance é a média ponderada dos objetivos que o desdobram.
              </p>
            ) : null}
            <Trend trend={p.trend} month={month} year={year} />
            <section aria-label="Principais desvios">
              <h2 className="text-sm font-semibold">Principais desvios do mês</h2>
              {p.deviations.length ? (
                <ol className="mt-2 flex flex-col gap-1.5">
                  {p.deviations.map((d) => (
                    <li key={d.kpiId} className="flex items-center justify-between gap-2">
                      <span className="min-w-0">
                        {seesHistory ? (
                          <Link
                            className="inline-flex items-center font-medium underline-offset-2 hover:underline"
                            to={drillHref.kpi(organizationId, {
                              projectId,
                              objectiveId: o.id,
                              kpiId: d.kpiId,
                              year,
                              month,
                            })}
                          >
                            {d.name}
                          </Link>
                        ) : (
                          <span className="font-medium">{d.name}</span>
                        )}
                        <span className="block text-xs text-muted-foreground tabular-nums">
                          {d.gap !== null
                            ? `${d.gap < 0 ? '−' : '+'}${withUnit(Math.abs(d.gap), d.unitLabel)} da meta`
                            : 'Sem meta ou realizado'}
                        </span>
                      </span>
                      <StatusBadge status={d.light} label={lightLabel(d.performance, d.light)} />
                    </li>
                  ))}
                </ol>
              ) : (
                <p className="mt-1 text-muted-foreground">Nenhum KPI abaixo da meta no mês.</p>
              )}
            </section>
            {p.children.length ? (
              <section aria-label="Objetivos que o desdobram" id={CHILDREN_ANCHOR} tabIndex={-1}>
                <h2 className="text-sm font-semibold">Desdobrado em</h2>
                <ul className="mt-2 flex flex-col gap-1.5">
                  {p.children.map((c) => (
                    <li key={c.id} className="flex items-center justify-between gap-2">
                      <Link className="min-w-0 underline-offset-2 hover:underline" to={link(c.id)}>
                        {c.name}
                      </Link>
                      {/* O número do objetivo que desdobra também leva a ele, no mesmo mês. */}
                      <DrillBadgeLink
                        to={link(c.id)}
                        label={`${c.name} ${lightLabel(c.performance, c.light)}: abrir o objetivo`}
                      >
                        <StatusBadge status={c.light} label={lightLabel(c.performance, c.light)} />
                      </DrillBadgeLink>
                    </li>
                  ))}
                </ul>
              </section>
            ) : null}
          </>
        )}
      </PanelState>
      <section aria-label="Relações do objetivo">
        <h2 className="text-sm font-semibold">Relações</h2>
        {relations.length ? (
          <ul className="mt-2 flex flex-col gap-1">
            {relations.map((r) => (
              <li key={`${r.direction}:${r.type}:${r.objective.id}`}>
                {OBJECTIVE_RELATION_LABELS[r.type]} {r.direction === 'para' ? '→' : '←'}{' '}
                <Link className="underline underline-offset-2" to={link(r.objective.id)}>
                  {r.objective.name}
                </Link>
              </li>
            ))}
          </ul>
        ) : (
          <p className="mt-1 text-muted-foreground">Sem relações com outros objetivos.</p>
        )}
      </section>
    </div>
  );
}

function KpisTab({
  organizationId,
  path,
  query,
}: {
  organizationId: string;
  /** O caminho até aqui: o KPI abre com o objetivo e o período (drill-down). */
  path: DrillPath;
  query: PanelQuery;
}) {
  // O histórico do indicador é da área Metas e Resultados: o atalho só para quem a acessa.
  const seesHistory = useCan()('metas-resultados', 'view');
  return (
    <PanelState query={query}>
      {(p) =>
        p.kpis.length === 0 ? (
          <EmptyState
            className="rounded-lg border border-dashed border-border py-10"
            icon={Target}
            title="Nenhum indicador mede este objetivo"
            description="Ligue indicadores ao objetivo no cadastro de KPIs: a performance do objetivo vem deles."
          />
        ) : (
          <ul aria-label="Indicadores do objetivo" className="flex flex-col gap-2 text-sm">
            {p.kpis.map((k) => {
              const name = (
                <>
                  <span className="font-mono text-xs text-muted-foreground">{k.code}</span> {k.name}
                </>
              );
              return (
                <li key={k.id} className="rounded-lg border border-border bg-surface p-3">
                  <article
                    aria-label={k.name}
                    className="flex flex-col gap-1 sm:flex-row sm:items-center sm:justify-between"
                  >
                    <div className="min-w-0">
                      {seesHistory ? (
                        <Link
                          className="inline-flex items-center gap-1 font-medium underline-offset-2 hover:underline"
                          to={drillHref.kpi(organizationId, { ...path, kpiId: k.id })}
                        >
                          {name}
                        </Link>
                      ) : (
                        <p className="font-medium">{name}</p>
                      )}
                      <dl className="mt-1 flex flex-wrap gap-x-4 text-xs text-muted-foreground tabular-nums">
                        <div className="flex gap-1">
                          <dt>Meta</dt>
                          <dd>{k.target !== null ? withUnit(k.target, k.unitLabel) : '—'}</dd>
                        </div>
                        <div className="flex gap-1">
                          <dt>Realizado</dt>
                          <dd>{k.actual !== null ? withUnit(k.actual, k.unitLabel) : '—'}</dd>
                        </div>
                        {k.weight !== null ? (
                          <div className="flex gap-1">
                            <dt>Peso</dt>
                            <dd>{k.weight}</dd>
                          </div>
                        ) : null}
                      </dl>
                    </div>
                    {seesHistory ? (
                      <DrillBadgeLink
                        to={drillHref.unit(organizationId, { ...path, kpiId: k.id })}
                        label={`${k.code} performance ${lightLabel(k.performance, k.light)}: ver por unidade`}
                      >
                        <StatusBadge
                          status={k.light}
                          label={`Performance ${lightLabel(k.performance, k.light)}`}
                        />
                      </DrillBadgeLink>
                    ) : (
                      <StatusBadge
                        status={k.light}
                        label={`Performance ${lightLabel(k.performance, k.light)}`}
                      />
                    )}
                  </article>
                </li>
              );
            })}
          </ul>
        )
      }
    </PanelState>
  );
}

const LIST_PAGE_SIZE = 50;

/** "Mostrando N de M" e "Carregar mais" das listas paginadas das abas. */
function MoreButton({
  shown,
  total,
  hasMore,
  loading,
  onMore,
}: {
  shown: number;
  total: number;
  hasMore: boolean;
  loading: boolean;
  onMore: () => void;
}) {
  if (!hasMore) return null;
  return (
    <div className="flex flex-wrap items-center gap-3 text-sm">
      <span className="text-muted-foreground">
        Mostrando {shown} de {total}
      </span>
      <Button variant="outline" size="sm" loading={loading} onClick={onMore}>
        Carregar mais
      </Button>
    </div>
  );
}

function InitiativesTab({
  organizationId,
  base,
  projectId,
  objectiveId,
}: {
  organizationId: string;
  base: string;
  projectId: string;
  objectiveId: string;
}) {
  const { client } = useAuth();
  const can = useCan();
  const allowed = can('iniciativas', 'view');
  const list = useInfiniteQuery({
    queryKey: ['initiatives', projectId, 'by-objective', objectiveId],
    queryFn: ({ pageParam, signal }) =>
      client.request(`${base}/initiatives`, {
        query: { objectiveId, page: pageParam, pageSize: LIST_PAGE_SIZE },
        schema: initiativeListSchema,
        signal,
      }),
    initialPageParam: 1,
    getNextPageParam: (last) => (last.page < last.totalPages ? last.page + 1 : undefined),
    enabled: allowed,
  });
  if (!allowed) return <p className="text-sm text-muted-foreground">Seu perfil não acessa as iniciativas.</p>;
  if (list.isPending) return <LoadingState rows={3} label="Carregando as iniciativas" />;
  if (list.isError) {
    return <ErrorState error={list.error} onRetry={() => void list.refetch()} retrying={list.isFetching} />;
  }
  const items = list.data.pages.flatMap((p) => p.items);
  if (!items.length) {
    return (
      <EmptyState
        className="rounded-lg border border-dashed border-border py-10"
        icon={Rocket}
        title="Nenhuma iniciativa"
        description="As iniciativas que contribuem para este objetivo aparecem aqui."
      />
    );
  }
  return (
    <div className="flex flex-col gap-3">
      <ul aria-label="Iniciativas do objetivo" className="flex flex-col gap-2 text-sm">
        {items.map((i) => (
          <li key={i.id} className="rounded-lg border border-border bg-surface p-3">
            <article aria-label={i.name} className="flex flex-col gap-1.5">
              <div className="flex flex-wrap items-center gap-2">
                <Link
                  className="font-medium underline-offset-2 hover:underline"
                  to={`/empresas/${organizationId}/execucao/iniciativas?projeto=${projectId}&iniciativa=${i.id}`}
                >
                  {i.name}
                </Link>
                <StatusBadge
                  status={INITIATIVE_STATUS_LIGHT[i.status]}
                  label={INITIATIVE_STATUS_LABELS[i.status]}
                />
                {i.late ? <StatusBadge status="red" label="Atrasada" /> : null}
              </div>
              <p className="text-xs text-muted-foreground">
                Líder: {i.leader?.name ?? 'sem líder'} ·{' '}
                {i.endDate ? `Prazo ${formatDate(i.endDate)}` : 'Sem prazo'}
              </p>
              <ProgressBar value={i.progress} label={`Progresso de ${i.name}`} className="max-w-md" />
            </article>
          </li>
        ))}
      </ul>
      <MoreButton
        shown={items.length}
        total={list.data.pages.at(-1)!.total}
        hasMore={list.hasNextPage}
        loading={list.isFetchingNextPage}
        onMore={() => void list.fetchNextPage()}
      />
    </div>
  );
}

function ActionsTab({
  organizationId,
  base,
  projectId,
  objectiveId,
}: {
  organizationId: string;
  base: string;
  projectId: string;
  objectiveId: string;
}) {
  const { client } = useAuth();
  const can = useCan();
  const allowed = can('planos-acao', 'view');
  const list = useInfiniteQuery({
    queryKey: ['action-plans', projectId, 'by-objective', objectiveId],
    queryFn: ({ pageParam, signal }) =>
      client.request(`${base}/action-plans`, {
        query: { objectiveId, page: pageParam, pageSize: LIST_PAGE_SIZE },
        schema: actionPlanListSchema,
        signal,
      }),
    initialPageParam: 1,
    getNextPageParam: (last) => (last.page < last.totalPages ? last.page + 1 : undefined),
    enabled: allowed,
  });
  if (!allowed)
    return <p className="text-sm text-muted-foreground">Seu perfil não acessa os planos de ação.</p>;
  if (list.isPending) return <LoadingState rows={3} label="Carregando os planos de ação" />;
  if (list.isError) {
    return <ErrorState error={list.error} onRetry={() => void list.refetch()} retrying={list.isFetching} />;
  }
  const plans = list.data.pages.flatMap((p) => p.items);
  if (!plans.length) {
    return (
      <EmptyState
        className="rounded-lg border border-dashed border-border py-10"
        icon={ListChecks}
        title="Nenhum plano de ação"
        description="Planos das iniciativas do objetivo e os gerados por alertas ou decisões sobre ele aparecem aqui."
      />
    );
  }
  return (
    <div className="flex flex-col gap-3">
      <ul aria-label="Planos de ação do objetivo" className="flex flex-col gap-2 text-sm">
        {plans.map((plan) => (
          <li key={plan.id} className="rounded-lg border border-border bg-surface p-3">
            <article aria-label={plan.name} className="flex flex-col gap-1.5">
              <Link
                className="font-medium underline-offset-2 hover:underline"
                to={`/empresas/${organizationId}/execucao/planos-acao?projeto=${projectId}&plano=${plan.id}`}
              >
                {plan.name}
              </Link>
              <p className="text-xs text-muted-foreground">
                Origem: {ACTION_ORIGIN_LABELS[plan.origin.type]} · Responsável:{' '}
                {plan.owner?.name ?? 'sem responsável'} ·{' '}
                {plan.summary.total === 1 ? '1 ação' : `${plan.summary.total} ações`}, {plan.summary.done}{' '}
                concluída(s)
                {plan.summary.late ? `, ${plan.summary.late} atrasada(s)` : ''}
              </p>
              {plan.summary.progress !== null ? (
                <ProgressBar
                  value={plan.summary.progress}
                  label={`Progresso de ${plan.name}`}
                  className="max-w-md"
                />
              ) : null}
            </article>
          </li>
        ))}
      </ul>
      <MoreButton
        shown={plans.length}
        total={list.data.pages.at(-1)!.total}
        hasMore={list.hasNextPage}
        loading={list.isFetchingNextPage}
        onMore={() => void list.fetchNextPage()}
      />
    </div>
  );
}

function HistoryTab({
  base,
  projectId,
  objectiveId,
  query,
  year,
  month,
}: {
  base: string;
  projectId: string;
  objectiveId: string;
  query: PanelQuery;
  year: number;
  month: number;
}) {
  const { client } = useAuth();
  const changes = useInfiniteQuery({
    queryKey: ['objectives', projectId, 'changes', objectiveId],
    queryFn: ({ pageParam, signal }) =>
      client.request(`${base}/objectives/${objectiveId}/changes`, {
        query: { limit: 20, ...(pageParam ? { cursor: pageParam } : {}) },
        schema: objectiveChangesSchema,
        signal,
      }),
    initialPageParam: '',
    getNextPageParam: (last) => last.nextCursor ?? undefined,
  });
  const items = changes.data?.pages.flatMap((p) => p.items) ?? [];
  return (
    <div className="flex flex-col gap-6 text-sm">
      <section aria-label="Série de performance">
        <h2 className="text-base font-bold">Série de performance de {year}</h2>
        <PanelState query={query}>
          {(p) =>
            p.trend.some((t) => t.performance !== null) ? (
              <>
                <Trend trend={p.trend} month={month} year={year} />
                <table className="mt-3 w-full max-w-md text-left text-sm">
                  <caption className="sr-only">Performance mensal de {year}</caption>
                  <thead>
                    <tr className="text-xs text-muted-foreground">
                      <th scope="col" className="py-1 font-medium">
                        Mês
                      </th>
                      <th scope="col" className="py-1 font-medium">
                        Performance
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {p.trend
                      .filter((t) => t.performance !== null)
                      .map((t) => (
                        <tr key={t.month} className="border-t border-border">
                          <td className="py-1">{targetPeriodLabel('MONTH', t.month)}</td>
                          <td className="py-1">
                            <StatusBadge status={t.light} label={lightLabel(t.performance, t.light)} />
                          </td>
                        </tr>
                      ))}
                  </tbody>
                </table>
              </>
            ) : (
              <p className="mt-1 text-muted-foreground">Sem performance calculada em {year}.</p>
            )
          }
        </PanelState>
      </section>
      <section aria-label="Alterações do objetivo" className="flex flex-col gap-2">
        <h2 className="text-base font-bold">Alterações</h2>
        {changes.isPending ? (
          <LoadingState rows={3} label="Carregando as alterações" />
        ) : changes.isError ? (
          <ErrorState
            error={changes.error}
            onRetry={() => void changes.refetch()}
            retrying={changes.isFetching}
          />
        ) : !items.length ? (
          <EmptyState
            className="py-8"
            icon={History}
            title="Nenhuma alteração registrada"
            description="As alterações do objetivo aparecem aqui, com quem, quando e o motivo."
          />
        ) : (
          <>
            <ol aria-label="Alterações, mais recentes primeiro" className="flex flex-col gap-2">
              {items.map((c) => (
                <li key={c.id} className="rounded-md border border-border px-3 py-2">
                  <p className="text-xs text-muted-foreground">
                    {formatDateTime(c.occurredAt)} · {c.actorName ?? 'Sistema'} ·{' '}
                    {AUDIT_SOURCE_LABELS[c.source] ?? c.source}
                    {c.action === 'CREATE' ? ' · cadastro' : ''}
                  </p>
                  <p className="mt-0.5 break-words">
                    <span className="font-semibold">{c.fieldLabel ?? 'Registro'}: </span>
                    {c.action === 'CREATE'
                      ? (c.newDisplay ?? '—')
                      : `${c.oldDisplay ?? '—'} → ${c.newDisplay ?? '—'}`}
                  </p>
                  {c.reason ? <p className="text-xs text-muted-foreground">Motivo: {c.reason}</p> : null}
                </li>
              ))}
            </ol>
            {changes.hasNextPage ? (
              <Button
                variant="outline"
                size="sm"
                className="self-start"
                loading={changes.isFetchingNextPage}
                onClick={() => void changes.fetchNextPage()}
              >
                Carregar mais
              </Button>
            ) : null}
          </>
        )}
      </section>
    </div>
  );
}
